import { useEffect, useState } from 'react';
import { Card } from '../../components/ui/card';
import { Loader2, EyeOff } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';
import { navSections } from '../../components/TrainingLayout';

const ROLE_LABEL = { admin: 'Admin', manager: 'Manager', training_manager: 'Training mgr', counsellor: 'Counsellor', sales_rep: 'Sales rep', user: 'User', restricted_user: 'Restricted' };
const when = v => new Date(v).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });

const flatten = s => s.items.flatMap(i => (i.groupKey ? i.children.map(c => ({ ...c, group: i.label })) : [i]));

const Row = ({ item, roles, hidden, onToggle, onAll }) => {
  const h = hidden[item.path] || [];
  const locked = item.path === '/training/menu-settings';
  return (
    <tr className="border-t border-slate-100" data-testid={`menu-row-${item.path}`}>
      <td className="px-4 py-2">
        <div className={`text-sm font-medium ${h.length === roles.length ? 'text-slate-400 line-through' : 'text-slate-900'}`}>{item.label}</div>
        <div className="text-[10px] text-slate-400">{item.group ? `${item.group} · ` : ''}{item.path}</div>
      </td>
      {roles.map(r => {
        const dis = locked && r === 'admin';
        return (
          <td key={r} className="text-center px-1">
            <input type="checkbox" checked={h.includes(r)} disabled={dis} onChange={() => onToggle(item, r)} className="w-4 h-4 accent-rose-600 cursor-pointer disabled:cursor-not-allowed" data-testid={`menu-hide-${item.path}-${r}`} title={`Hide for ${ROLE_LABEL[r]}`} />
          </td>
        );
      })}
      <td className="px-2 text-right whitespace-nowrap">
        {!locked && <button onClick={() => onAll(item, h.length === roles.length ? [] : roles)} className="text-[11px] text-violet-700 hover:underline" data-testid={`menu-all-${item.path}`}>{h.length === roles.length ? 'Show for all' : 'Hide for all'}</button>}
      </td>
    </tr>
  );
};

export default function MenuSettingsPage() {
  const [d, setD] = useState(null);
  const [log, setLog] = useState([]);
  const loadLog = () => api.get('/nav-visibility/log').then(r => setLog(r.data.items));
  useEffect(() => { api.get('/nav-visibility').then(r => setD(r.data)); loadLog(); }, []);
  const save = async (item, roles) => {
    try {
      const r = await api.put('/nav-visibility', { path: item.path, label: item.label, roles });
      setD(x => ({ ...x, hidden: r.data.hidden }));
      window.dispatchEvent(new Event('nav-visibility-changed'));
      toast.success(roles.length ? `${item.label} hidden for ${roles.map(x => ROLE_LABEL[x]).join(', ')}` : `${item.label} shown for everyone`);
      loadLog();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not save'); }
  };
  if (!d) return <div className="py-20 text-center"><Loader2 className="w-5 h-5 animate-spin inline" /></div>;
  const toggle = (item, role) => {
    const h = d.hidden[item.path] || [];
    save(item, h.includes(role) ? h.filter(x => x !== role) : [...h, role]);
  };
  return (
    <div className="space-y-5" data-testid="menu-settings-page">
      <div>
        <h1 className="text-2xl font-bold text-slate-900 tracking-tight flex items-center gap-2"><EyeOff className="w-5 h-5 text-violet-600" />Menu Settings</h1>
        <p className="text-sm text-slate-500">Tick a role to hide that menu item from it. Pages are only hidden from the menu, not deleted — untick to bring them back. Role rules already built into the app still apply.</p>
      </div>
      {navSections.map(s => (
        <Card key={s.key} className="overflow-x-auto" data-testid={`menu-section-${s.key}`}>
          <table className="w-full table-fixed">
            <colgroup><col />{d.roles.map(r => <col key={r} className="w-[84px]" />)}<col className="w-[100px]" /></colgroup>
            <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
              <tr><th className="text-left px-4 py-2">{s.label} — hide for</th>{d.roles.map(r => <th key={r} className="px-1 font-semibold">{ROLE_LABEL[r]}</th>)}<th /></tr>
            </thead>
            <tbody>{flatten(s).map(i => <Row key={i.path} item={i} roles={d.roles} hidden={d.hidden} onToggle={toggle} onAll={save} />)}</tbody>
          </table>
        </Card>
      ))}
      <Card className="p-4 space-y-2" data-testid="menu-settings-log">
        <div className="text-sm font-bold text-slate-900">Change history</div>
        {log.length === 0 ? <p className="text-xs text-slate-400">No changes yet.</p> : log.map(l => (
          <div key={l.id} className="text-xs border-l-2 border-violet-200 pl-2">
            <span className="font-medium text-slate-800">{l.label}</span>: hidden for {l.before.length ? l.before.map(x => ROLE_LABEL[x]).join(', ') : 'nobody'} → {l.after.length ? l.after.map(x => ROLE_LABEL[x]).join(', ') : 'nobody'}
            <span className="text-slate-400"> · {l.by} · {when(l.at)}</span>
          </div>
        ))}
      </Card>
    </div>
  );
}
